import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, Crown, Check, X } from "lucide-react";
import { resolvePlan, type ResolvedPlan } from "@/lib/billing/resolvePlan";
import { trackEvent } from "@/lib/audit";
import { useLanguage } from "@/hooks/useLanguage";
import { LanguageDropdown } from "@/components/i18n/LanguageDropdown";

export const Route = createFileRoute("/mi-plan")({
  head: () => ({
    meta: [
      { title: "Mi Plan · VerseSport™" },
      { name: "description", content: "Consulta tu plan actual y los beneficios incluidos en VerseSport™." },
    ],
  }),
  component: MyPlanPage,
});

function MyPlanPage() {
  const [email, setEmail] = useState("");
  const [plan, setPlan] = useState<ResolvedPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();

  const load = async (e?: string) => {
    setLoading(true);
    const p = await resolvePlan({ email: e });
    setPlan(p);
    setLoading(false);
    void trackEvent("my_plan_viewed", { plan: p.plan_code, source: p.source });
  };

  useEffect(() => {
    void load();
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border/60 glass">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3">
          <Link to="/" className="flex h-9 w-9 items-center justify-center rounded-lg glass" aria-label={t("back")}>
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div>
            <div className="text-sm font-black tracking-tight flex items-center gap-2">
              <Crown className="h-4 w-4 text-primary" /> {t("my_plan")}
            </div>
            <div className="text-[10px] font-bold tracking-widest text-muted-foreground">
              VERSESPORT™
            </div>
          </div>
          <div className="ml-auto"><LanguageDropdown /></div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl space-y-4 px-4 py-6">
        <div className="rounded-2xl glass p-4">
          <label className="text-[11px] font-bold text-muted-foreground">{t("email_optional")}</label>
          <div className="mt-2 flex gap-2">
            <input
              className="flex-1 rounded-md bg-background/50 px-3 py-2 text-sm"
              placeholder="tu@correo.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button
              onClick={() => load(email)}
              className="rounded-md bg-primary px-4 py-2 text-sm font-bold text-primary-foreground"
            >
              {t("query")}
            </button>
          </div>
        </div>

        {loading && <p className="text-sm text-muted-foreground">{t("plan_resolving")}</p>}

        {plan && (
          <div className="rounded-2xl glass p-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] font-bold tracking-widest text-muted-foreground">{t("current_plan")}</div>
                <div className="text-2xl font-black uppercase">{plan.plan_code.replace("_", " ")}</div>
                <div className="text-[11px] text-muted-foreground">{t("plan_source")}: {plan.source}</div>
              </div>
              <Crown className="h-10 w-10 text-primary" />
            </div>

            {plan.expires_at && (
              <p className="mt-2 text-[12px] text-amber-400">
                {t("plan_expires")}: {new Date(plan.expires_at).toLocaleDateString()}
              </p>
            )}

            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              <Benefit ok={plan.premium_enabled} label={t("benefit_premium")} />
              <Benefit ok={plan.advanced_predictions_enabled} label={t("benefit_predictions")} />
              <Benefit ok={plan.historical_analogs_enabled} label={t("benefit_history")} />
              <Benefit ok={plan.decisive_commentary_enabled} label={t("benefit_decisive")} />
              <Benefit ok={plan.rewards_premium_enabled} label={t("benefit_rewards")} />
              <Benefit ok={plan.ad_free_enabled} label={t("benefit_adfree")} />
              <Benefit ok={plan.enterprise_features_enabled} label={t("benefit_enterprise")} />
              <Benefit
                ok={true}
                label={
                  plan.ai_messages_per_day < 0
                    ? t("benefit_ai_unlimited")
                    : t("benefit_ai_quota", { n: plan.ai_messages_per_day })
                }
              />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function Benefit({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-border/60 px-3 py-2 text-[12px]">
      {ok ? (
        <Check className="h-4 w-4 text-[oklch(0.82_0.24_145)]" />
      ) : (
        <X className="h-4 w-4 text-muted-foreground" />
      )}
      <span className={ok ? "" : "text-muted-foreground line-through"}>{label}</span>
    </div>
  );
}
